import * as zoid from "zoid/dist/zoid.frameworks";

// In-house zoid component contract for the team-management embed.
//
// Owned by this app (not shared via @ahaslides-product/plugins-ui-vanilla)
// so the contract can evolve at this app's cadence without depending on
// publishes of a shared package. The host (presenter) defines a matching
// schema at `src/plugins/zoid/teamAppZoid.js` under the same tag — keep
// them in lockstep.

export type TeamAppConfirmModalPayload = {
  title: string;
  content: string;
  okText?: string;
  cancelText?: string;
  variant?: "primary" | "danger";
};

export type TeamAppToastFn = (
  text: string,
  uniqName?: string,
  action?: unknown,
  options?: unknown,
) => void;

export interface TeamAppCurrentUser {
  id?: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
  accountId?: string;
  /** Role of this user inside their team — owner / admin / editor / viewer. */
  accountRole?: string;
  /**
   * System-level role from the User record — distinct from `accountRole`.
   * `'admin'` / `'master'` mean this is an internal CS / staff account
   * (only relevant when the embed is mounted from the admin-app, e.g.
   * the `/admin/teams/:accountId` route). Regular users have `'user'`
   * or undefined.
   */
  role?: string;
  /** Preferred presenter language (e.g. "en", "vi"). */
  presenterLanguage?: string;
  planType?: string;
  whiteLabelPresenterDomain?: string;
  whiteLabelLink?: string;
}

/**
 * Shape of one Unleash feature-flag entry, mirroring the presenter's
 * `state.features[name]` slot. We pass the entire flag dict through so
 * the embed can resolve any flag without needing a new zoid prop per
 * feature — same pattern as `ReportIframe.vue`. Use the
 * `useHostFeatureFlag(name)` hook for callers; this type stays here so
 * the contract is one place.
 */
export interface TeamAppFeatureFlag {
  enabled?: boolean;
  variant?: {
    name?: string;
    enabled?: boolean;
    payload?: unknown;
  };
}
export type TeamAppFeatureFlags = Record<string, TeamAppFeatureFlag>;

/**
 * AHA-41755 bulk-action bridge.
 *
 * The embed can't render a viewport-sticky bottom bar (the iframe's height
 * is sized to its content, so `position: fixed; bottom: 0` lands wherever
 * the iframe ends, not at the bottom of the browser window). The host
 * (presenter) owns the bar. Communication is two-way through zoid:
 *
 *   embed → host:  call `onSelectionChange(state)` whenever the user
 *                  ticks/unticks rows. State carries the cohort counts
 *                  the bar uses to decide which buttons to render.
 *   host → embed:  set `bulkActionRequest` via `zoidInstance.updateProps`
 *                  when the user clicks an action (after the presenter-side
 *                  confirm modal). Embed watches via `xprops.onProps`
 *                  and runs the assign/revoke loop for the matching cohort.
 *                  A fresh `nonce` per request lets the embed distinguish
 *                  "new request" from a re-render of an old one.
 */
export interface BulkSelectionState {
  /** Member IDs currently ticked (includes both assignable and revocable). */
  selectedIds: string[];
  /** Subset of selection that would be granted a licence by an "Assign"
   *  action — unlicensed, non-personal-plan rows. */
  assignableCount: number;
  /** Subset of selection that would be revoked by a "Revoke" action —
   *  licensed, non-personal-plan rows. */
  revocableCount: number;
  /** AHA-46193: remaining assignable licence seats =
   *  max(0, licenseQuantity - numberOfUsedLicenses). The host disables the
   *  bulk "Assign" button when this is 0 — `assignableCount` alone (how many
   *  selected rows *would* be granted) doesn't tell the host whether any
   *  seats are actually free, so with 0 seats and an unlicensed member ticked
   *  the button used to stay enabled. Contract must match the presenter host
   *  exactly (field name, `number`, same semantics). */
  availableLicenses: number;
}

export interface BulkActionRequest {
  /** Random per-click ID. Embed watches for changes here to detect a new
   *  dispatch — same value across re-renders = same request, ignore. */
  nonce: string;
  /** "assign" / "revoke" → run the cohort loop and toast a summary.
   *  "clear" → drop the embed-side selection (used by the bar's X close);
   *  without this channel the X would just hide the host's local copy and
   *  the bar would pop back the moment the user touches any checkbox. */
  action: "assign" | "revoke" | "clear";
}

export interface TeamAppProps {
  /** Iframe URL — supplied by the host based on the per-env deploy. */
  url: string;
  /** Host session token forwarded for the embed's auth swap. */
  token?: string;
  /** User identity hydrated by the host. */
  currentUser?: TeamAppCurrentUser;
  /**
   * Live UI locale from the host (vuex `presenterLocale`) — updated via
   * zoid's `updateProps` whenever the user switches language in the
   * presenter. Distinct from `currentUser.presenterLanguage`, which is
   * the persisted profile setting and doesn't reflect runtime changes.
   */
  currentLanguage?: string;

  /** Toast bridges. */
  showToastSuccess?: TeamAppToastFn;
  showToastError?: TeamAppToastFn;
  showToastInfo?: TeamAppToastFn;
  /** Confirm dialog — resolves true if the user confirmed. */
  showConfirmModal?: (payload: TeamAppConfirmModalPayload) => Promise<boolean>;
  /** Analytics passthrough — matches the presenter's
   *  `Util.trackGA4AndMixpanel(eventName, payload?)` signature. */
  trackGA4AndMixpanel?: (
    eventName: string,
    payload?: Record<string, unknown>,
  ) => void;

  /**
   * Ask the host to navigate its outer router to `location` — used for
   * in-host redirects from the embed (e.g. Buy-more-licenses links).
   */
  onRouteChange?: (payload: {
    location: string;
    navType: "push" | "pop" | "replace";
    fullScreen: boolean;
  }) => void;
  /**
   * Child reports its content height in px so the host can size the
   * iframe. `null` means "use 100%".
   */
  onHeightChange?: (height: number | null) => void;
  /**
   * Run reCAPTCHA v3 on the host side and return a token to the child.
   * Keeps reCAPTCHA key + script load registered on the presenter's
   * domain instead of duplicating it here.
   */
  executeRecaptcha?: (action: string) => Promise<string | null>;
  /**
   * Open the host's "Request additional licenses" modal — the form that
   * sends a Slack notification to the CS team instead of taking the
   * user to Stripe checkout. Used for teams created/managed by customer
   * success (`isTeamCreatedByCustomerSuccess`), where direct self-serve
   * checkout would bypass the agreed billing arrangement. The host owns
   * the form (presenter `RequestAdditionalLicenseModal.vue`) so the
   * embed doesn't re-implement pricing API calls or duplicate the UX.
   * See AHA-42841.
   */
  openRequestAdditionalLicenseModal?: () => void;
  /**
   * Whether the team is provisioned via SCIM (computed by the host as
   * `currentUser.isScimEnabled && canUseSCIM`). SCIM teams generally
   * have member-management actions hidden — but the
   * `AHA-42627-scim-manual-management` flag overrides that for special
   * teams (ATB et al). The embed reads the flag from `featureFlags`.
   * See AHA-43040.
   */
  scimEnabled?: boolean;
  /**
   * Full Unleash flag dictionary (matches presenter `state.features`
   * shape from `featureToggles.module.js`). Forwarded verbatim so the
   * embed can resolve arbitrary flags without a new zoid prop per
   * feature — see `useHostFeatureFlag`. Mirrors `ReportIframe.vue`.
   */
  featureFlags?: TeamAppFeatureFlags;

  /**
   * AHA-41755 bulk-action bridge — see BulkSelectionState / BulkActionRequest
   * docs above. `onSelectionChange` is invoked by the embed every time the
   * tick-box selection on the members table changes; `bulkActionRequest` is
   * pushed live from the host via `zoidInstance.updateProps` to dispatch an
   * action (after the host-side confirm modal accepts).
   */
  onSelectionChange?: (state: BulkSelectionState) => void;
  bulkActionRequest?: BulkActionRequest | null;
  /** Fired once the embed finishes running a bulk action (success or
   *  failure) — lets the host clear its in-flight spinner and re-enable
   *  the action buttons. Pairs with `bulkActionRequest`: every dispatched
   *  request results in exactly one onBulkActionComplete. */
  onBulkActionComplete?: () => void;
}

/**
 * Register the team-app zoid component using the `'team-app-iframe'`
 * tag. Called once on bootstrap from `main.tsx`. The host
 * (`stpancras-presenter-app`) registers the same tag with a matching
 * prop schema; zoid wires the parent-child handshake by tag.
 *
 * Permissions Policy grants from the host iframe (set via
 * `attributes.iframe.allow` on the host-side zoid.create):
 *   - clipboard-write — `navigator.clipboard.writeText` (invitation link)
 *
 * If a new browser-restricted API is needed, add the permission token
 * on the host side and document it here.
 */
export function initializeTeamApp() {
  return zoid.create({
    tag: "team-app-iframe",
    url: ({ props }: { props: TeamAppProps }) => props.url,
    props: {
      url: { type: "string", required: true, queryParam: false },
      token: { type: "string", required: false, queryParam: false },
      currentUser: { type: "object", required: false, queryParam: false },
      currentLanguage: { type: "string", required: false, queryParam: false },
      showToastSuccess: { type: "function", required: false },
      showToastError: { type: "function", required: false },
      showToastInfo: { type: "function", required: false },
      showConfirmModal: { type: "function", required: false },
      trackGA4AndMixpanel: { type: "function", required: false },
      onRouteChange: { type: "function", required: false },
      onHeightChange: { type: "function", required: false },
      executeRecaptcha: { type: "function", required: false },
      openRequestAdditionalLicenseModal: { type: "function", required: false },
      scimEnabled: { type: "boolean", required: false, queryParam: false },
      featureFlags: { type: "object", required: false, queryParam: false },
      // AHA-41755 bulk-action bridge.
      onSelectionChange: { type: "function", required: false },
      bulkActionRequest: { type: "object", required: false, queryParam: false },
      onBulkActionComplete: { type: "function", required: false },
    },
  });
}

/**
 * True once zoid has populated `window.xprops` with the parent-supplied
 * props (after the iframe handshake completes).
 */
export function isTeamAppInitialized(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean((window as { xprops?: TeamAppProps }).xprops)
  );
}

/**
 * Return the zoid `xprops` typed as `TeamAppProps`. Throws if the app
 * isn't initialized — most callers want `getHost()` (returns `null`
 * outside zoid) instead.
 */
export function getTeamApp(): TeamAppProps {
  if (!isTeamAppInitialized()) {
    throw new Error(
      "Team app is not initialized: window.xprops is not available.",
    );
  }
  return (window as { xprops?: TeamAppProps }).xprops as TeamAppProps;
}
